import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { css } from '@emotion/react';
import { PinComponentType } from '@lessonup/pins-shared';
import {
  Button,
  color,
  IconClose,
  IconDragIndicator,
  ListItem,
  SpaceBetween,
  spacing,
  styled,
  Truncate,
} from '@lessonup/ui-components';
import React, { PropsWithChildren } from 'react';
import { PinComponentTypeIcon } from './PinComponentTypeIcon';

export interface AnimationListItemProps {
  sortableId: string;
  label: string;
  deleteAnimation?(): void;
  isSelected?: boolean;
  isHighlighted?: boolean;
  onMouseEnter?(): void;
  onMouseLeave?(): void;
  onPointerDown?(): void;
  componentType: PinComponentType;
}

export function AnimationListItem(props: PropsWithChildren<AnimationListItemProps>) {
  const {
    sortableId,
    label,
    componentType,
    deleteAnimation,
    isSelected = false,
    isHighlighted = false,
    onMouseEnter,
    onMouseLeave,
    onPointerDown,
  } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortableId });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition: transition ?? undefined,
        zIndex: isDragging ? 1 : undefined,
      }}
    >
      <StyledListItem
        text={
          <SpaceBetween direction="x" spacing={spacing.size8} alignItems="center">
            <StyledIconComponentType componentType={componentType} />
            <Truncate maxLines={1} wordBreak="break-all">
              {label}
            </Truncate>
          </SpaceBetween>
        }
        startSlot={
          <DragHandle>
            <IconDragIndicator />
          </DragHandle>
        }
        endSlot={
          <DeleteButton
            iconStart={<IconClose />}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={deleteAnimation}
            buttonType="neutral"
            isSelected={isSelected}
            size="small"
          />
        }
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        type={isSelected ? 'active' : 'default'}
        indentation={true}
        roundedCorners={true}
        isHighlighted={isHighlighted}
        isSelected={isSelected}
        secondLine={props.children}
        {...listeners}
        {...attributes}
        // Overwrite `onPointerDown` from the Sortable `listeners`
        onPointerDown={(e) => {
          listeners?.onPointerDown?.(e);
          onPointerDown?.();
        }}
      />
    </div>
  );
}

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${color.additional.disabled.text};
  padding: ${spacing.size4};
  margin-right: ${spacing.size4};
`;

const DeleteButton = styled(Button)<{ isSelected: boolean }>`
  background-color: transparent;

  // Override the hover colour to better contrast with the selected list item.
  ${({ isSelected }) =>
    isSelected &&
    css`
      &:hover {
        background-color: white;
      }
    `}
`;

const highlightedStyles = css`
  &:not(:has(button:hover, label:hover)) {
    background-color: rgb(245, 246, 247); /* color.neutral.surface.hover without the alpha on white background */
  }
`;

const StyledListItem = styled(ListItem)<{ isHighlighted: boolean; isSelected: boolean }>`
  cursor: grab;
  touch-action: none;
  user-select: none;

  &:not(:hover) button {
    visibility: ${({ isSelected }) => (isSelected ? 'visible' : 'hidden')};
  }

  ${({ isHighlighted }) => isHighlighted && highlightedStyles}
`;

const StyledIconComponentType = styled(PinComponentTypeIcon)`
  flex-shrink: 0;
`;
